import React, { useState } from "react";
import styled from "styled-components";
import ConfirmationModal from "../Common/ConfirmationModal";
import API from "../../util/api";

const ModalOverlay = styled.div`
  position: fixed;
  top: 0; left: 0;
  width:100%; height:100%;
  background: rgba(0,0,0,0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContainer = styled.div`
  background: #fff;
  border-radius: 8px;
  width: 600px;
  max-width: 90%;
  box-shadow: 0 2px 10px rgba(0,0,0,0.3);
  overflow: hidden;
`;

const Header = styled.div`
  background-color: ${(p) => p.colors.buttonBackground};
  color: ${(p) => p.colors.buttonText};
  padding: 12px 20px;
  font-size: 16px;
  font-weight: bold;
`;

const Body = styled.div`
  padding: 20px;
`;

const FieldRow = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 12px;
`;

const Label = styled.label`
  width: 140px;
  font-weight: bold;
  font-size: 13px;
`;

const Input = styled.input`
  flex: 1;
  padding: 6px 8px;
  font-size: 13px;
  border: 1px solid #ccc;
  border-radius: 4px;
`;

const ButtonRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
`;

const Button = styled.button`
  padding: 8px 16px;
  font-size: 14px;
  color: #fff;
  background: ${(p) => p.colors.buttonBackground};
  border: none;
  border-radius: 4px;
  cursor: pointer;
  &:hover { background: ${(p) => p.colors.buttonHoverBackground}; }
`;

function formatDate(d) {
  const date = new Date(d);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export default function UpdateImplementationModal({
  show,
  colors,
  implementation,
  onCancel,
  onSuccess,
}) {

  // use the implementation's own dates
  const minDate = implementation.start_date;
  const maxDate = implementation.end_date;

  // certified may be a datetime string; just take YYYY-MM-DD
  const initialCert = implementation.certified
    ? implementation.certified.split(" ")[0]
    : "";

  const [startDate, setStartDate]     = useState(minDate);
  const [endDate, setEndDate]         = useState(maxDate);
  const [numStudents, setNumStudents] = useState(
    implementation.num_of_students ?? ""
  );
  const [certDate, setCertDate]       = useState(initialCert);

  const [confirmOpen, setConfirmOpen]   = useState(false);
  const [resultOpen, setResultOpen]     = useState(false);
  const [resultMsg, setResultMsg]       = useState("");
  const [resultStatus, setResultStatus] = useState("Success");

  const resultFailed = resultStatus !== "Success";

  const handleSubmit = () => setConfirmOpen(true);

  const handleConfirm = async () => {
    setConfirmOpen(false);
    try {
      const resp = await API.updateTeacherImplementation({
        user_id:        implementation.user_id,
        implementation_id: implementation.implementation_id,
        start_date:        startDate,
        end_date:          endDate,
        num_of_students:   numStudents,
      });
      const status = resp?.status === "Failed" ? "Failed" : "Success";
      setResultMsg(resp.message || (status === "Failed" ? "Update failed." : "Updated successfully."));
      setResultStatus(status);
    } catch (err) {
      setResultMsg(err.message || "Update failed.");
      setResultStatus("Failed");
    } finally {
      setResultOpen(true);
    }
  };

  const handleResultClose = () => {
    setResultOpen(false);
    if (!resultFailed && typeof onSuccess === "function") {
      onSuccess();
    }
    setResultStatus("Success");
  };

  return (
    <ModalOverlay>
      <ModalContainer>
        <Header colors={colors}>Update Implementation</Header>
        <Body>
          {/* new header line */}
          <p style={{ marginBottom: "1em", fontSize: "14px" }}>
            <strong>
              Implementation for {implementation.org_name}, {implementation.name}
            </strong>
          </p>

          {/* date constraint note */}
          {/* <p style={{ marginBottom: "1.5em", color: "#555", fontSize: "12px" }}>
            Start and End Dates must be between{" "}
            <strong>{formatDate(minDate)}</strong> and{" "}
            <strong>{formatDate(maxDate)}</strong>
          </p> */}

          {/* <FieldRow>
            <Label>Start Date:</Label>
            <Input
              type="date"
              value={startDate}
              min={minDate}
              max={maxDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </FieldRow>

          <FieldRow>
            <Label>End Date:</Label>
            <Input
              type="date"
              value={endDate}
              min={minDate}
              max={maxDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </FieldRow> */}

          <FieldRow>
            <Label>Number of Students:</Label>
            <Input
              type="number"
              value={numStudents}
              onChange={(e) => setNumStudents(e.target.value)}
            />
          </FieldRow>

          <FieldRow>
            <Label>Certified Date:</Label>
            <Input
              type="date"
              value={certDate}
              onChange={(e) => setCertDate(e.target.value)}
              disabled
            />
          </FieldRow>

          <ButtonRow>
            <Button colors={colors} onClick={onCancel}>
              Cancel
            </Button>
            <Button colors={colors} onClick={handleSubmit}>
              Update
            </Button>
          </ButtonRow>
        </Body>
      </ModalContainer>

      {/* confirm before calling API */}
      <ConfirmationModal
        show={confirmOpen}
        title="Confirm Update"
        message="Are you sure you want to update this implementation?"
        onCancel={() => setConfirmOpen(false)}
        onConfirm={handleConfirm}
      />

      {/* show result status */}
      <ConfirmationModal
        show={resultOpen}
        title={resultFailed ? "Error" : "Success"}
        message={resultMsg}
        onCancel={handleResultClose}
        onConfirm={handleResultClose}
        status={resultStatus}
        single
      />
    </ModalOverlay>
  );
}
